import { transformError as sdkTransformError } from '@wix/sdk-runtime/transform-error';
import { queryBuilder } from '@wix/sdk-runtime/query-builder';
import {
  renameKeysFromSDKRequestToRESTRequest,
  renameKeysFromRESTResponseToSDKResponse,
} from '@wix/sdk-runtime/rename-all-nested-keys';
import {
  HttpClient,
  HttpResponse,
  NonNullablePaths,
  QuerySpec,
  Query as QuerySdkType,
} from '@wix/sdk-types';
import * as ambassadorWixEcomV1ShippingOption from './ecom-v1-shipping-option-shipping-options.http.js';
// @ts-ignore
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { createQueryUtils } from '@wix/sdk-runtime/query-builder-utils';

export interface ShippingOption {
  /**
   * ShippingOption ID.
   * @format GUID
   * @readonly
   */
  _id?: string | null;
  /**
   * Revision number, which increments by 1 each time the ShippingOption is updated.
   * To prevent conflicting changes,
   * the current revision must be passed when updating the ShippingOption.
   *
   * Ignored when creating a ShippingOption.
   * @readonly
   */
  revision?: string | null;
  /**
   * Date and time the ShippingOption was created.
   * @readonly
   */
  _createdDate?: Date | null;
  /**
   * Date and time the ShippingOption was last updated.
   * @readonly
   */
  _updatedDate?: Date | null;
  /**
   * Associated delivery region id.
   * @format GUID
   * @immutable
   * @deprecated Associated delivery region id.
   * @replacedBy delivery_region_ids
   * @targetRemovalDate 2025-06-01
   */
  deliveryRegionId?: string;
  /**
   * This shipping option is active for the following delivery regions.
   * @maxSize 50
   * @format GUID
   * @immutable
   */
  deliveryRegionIds?: string[];
  /**
   * Delivery option configured for the ShippingOption
   * @maxLength 1000
   */
  title?: string | null;
  /**
   * Estimated delivery time. For example, "2-3 business days".
   * @maxLength 500
   */
  estimatedDeliveryTime?: string | null;
  /**
   * at runtime for a given shipment input, up to one rate (price) should be returned in an option. If more than one rate is valid then we return the lowest one.
   * @minSize 1
   * @maxSize 50
   */
  rates?: ConditionalRates[];
  /** Data Extensions */
  extendedFields?: ExtendedFields;
}

export interface ConditionalRates {
  /**
   * there is an AND logic between all the conditions. Empty conditions means true.
   * The amount of the rate that will be returned if all conditions are met.
   * @decimalValue options { gte:0, lte:999999999, maxScale:3 }
   */
  amount?: string;
  /**
   * For example: weight > 0 and weight <= 10
   * @maxSize 10
   */
  conditions?: Condition[];
  /** When this flag is set to true, multiply the amount by the number of line items passed on the request. */
  multiplyByQuantity?: boolean;
}

export interface Condition {
  type?: ConditionTypeWithLiterals;
  /**
   * The value in respective to the condition type
   * Weight values should be in the same weight units of the store: KG / LB
   * Total price is according to the store currency
   * Quantity of items should be integers
   * @decimalValue options { gte:0, lte:999999999, maxScale:6 }
   */
  value?: string;
  /** Logical operator. */
  operator?: LogicalOperatorWithLiterals;
}

export enum ConditionType {
  BY_TOTAL_WEIGHT = 'BY_TOTAL_WEIGHT',
  BY_TOTAL_PRICE = 'BY_TOTAL_PRICE',
  BY_TOTAL_QUANTITY = 'BY_TOTAL_QUANTITY',
}

/** @enumType */
export type ConditionTypeWithLiterals =
  | ConditionType
  | 'BY_TOTAL_WEIGHT'
  | 'BY_TOTAL_PRICE'
  | 'BY_TOTAL_QUANTITY';

export enum LogicalOperator {
  EQ = 'EQ',
  GT = 'GT',
  GTE = 'GTE',
  LT = 'LT',
  LTE = 'LTE',
}

/** @enumType */
export type LogicalOperatorWithLiterals =
  | LogicalOperator
  | 'EQ'
  | 'GT'
  | 'GTE'
  | 'LT'
  | 'LTE';

export interface ExtendedFields {
  /**
   * Extended field data. Each key corresponds to the namespace of the app that created the extended fields.
   * The value of each key is structured according to the schema defined when the extended fields were configured.
   *
   * You can only access fields for which you have the appropriate permissions.
   *
   * Learn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).
   */
  namespaces?: Record<string, Record<string, any>>;
}

export interface CreateShippingOptionRequest {
  /** ShippingOption to be created. */
  shippingOption: ShippingOption;
}

export interface CreateShippingOptionResponse {
  /** The created ShippingOption. */
  shippingOption?: ShippingOption;
}

export interface GetShippingOptionRequest {
  /**
   * ID of the ShippingOption to retrieve.
   * @format GUID
   */
  shippingOptionId: string;
}

export interface GetShippingOptionResponse {
  /** The requested ShippingOption. */
  shippingOption?: ShippingOption;
}

export interface UpdateShippingOptionRequest {
  /** ShippingOption to be updated, may be partial. */
  shippingOption: ShippingOption;
  /**
   * Set of fields to update.
   *
   * Fields that aren't included in `fieldMask.paths` are ignored.
   * @internal
   */
  fieldMask?: string[];
}

export interface UpdateShippingOptionResponse {
  /** Updated ShippingOption. */
  shippingOption?: ShippingOption;
}

export interface DeleteShippingOptionRequest {
  /**
   * Id of the ShippingOption to delete.
   * @format GUID
   */
  shippingOptionId: string;
}

export interface DeleteShippingOptionResponse {}

export interface QueryShippingOptionsRequest {
  /** WQL expression. */
  query?: CursorQuery;
}

export interface CursorQuery extends CursorQueryPagingMethodOneOf {
  /** Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`. */
  cursorPaging?: CursorPaging;
  /**
   * Filter object in the following format:
   * `"filter" : {
   * "fieldName1": "value1",
   * "fieldName2":{"$operator":"value2"}
   * }`
   * Example of operators: `$eq`, `$ne`, `$lt`, `$lte`, `$gt`, `$gte`, `$in`, `$hasSome`, `$hasAll`, `$startsWith`, `$contains`
   */
  filter?: Record<string, any> | null;
  /**
   * Sort object in the following format:
   * `[{"fieldName":"sortField1","order":"ASC"},{"fieldName":"sortField2","order":"DESC"}]`
   * @maxSize 5
   */
  sort?: Sorting[];
}

/** @oneof */
export interface CursorQueryPagingMethodOneOf {
  /** Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`. */
  cursorPaging?: CursorPaging;
}

export interface Sorting {
  /**
   * Name of the field to sort by.
   * @maxLength 512
   */
  fieldName?: string;
  /** Sort order. */
  order?: SortOrderWithLiterals;
}

export enum SortOrder {
  ASC = 'ASC',
  DESC = 'DESC',
}

/** @enumType */
export type SortOrderWithLiterals = SortOrder | 'ASC' | 'DESC';

export interface CursorPaging {
  /**
   * Maximum number of items to return in the results.
   * @max 100
   */
  limit?: number | null;
  /**
   * Pointer to the next or previous page in the list of results.
   *
   * Pass the relevant cursor token from the `pagingMetadata` object in the previous call's response.
   * Not relevant for the first request.
   * @maxLength 16000
   */
  cursor?: string | null;
}

export interface QueryShippingOptionsResponse {
  /**
   * List of ShippingOptions.
   * @maxSize 100
   */
  shippingOptions?: ShippingOption[];
  /** Paging metadata */
  pagingMetadata?: CursorPagingMetadata;
}

export interface CursorPagingMetadata {
  /** Number of items returned in the response. */
  count?: number | null;
  /** Cursor strings that point to the next page, previous page, or both. */
  cursors?: Cursors;
  /**
   * Whether there are more pages to retrieve following the current page.
   *
   * + `true`: Another page of results can be retrieved.
   * + `false`: This is the last page.
   */
  hasNext?: boolean | null;
}

export interface Cursors {
  /**
   * Cursor string pointing to the next page in the list of results.
   * @maxLength 16000
   */
  next?: string | null;
  /**
   * Cursor pointing to the previous page in the list of results.
   * @maxLength 16000
   */
  prev?: string | null;
}

export interface UpdateExtendedFieldsRequest {
  /** ID of the entity to update. */
  _id: string;
  /** Identifier for the app whose extended fields are being updated. */
  namespace: string;
  /** Data to update. Structured according to the [schema](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields#json-schema-for-extended-fields) defined when the extended fields were configured. */
  namespaceData: Record<string, any> | null;
}

export interface UpdateExtendedFieldsResponse {
  /** Updated ShippingOption. */
  shippingOption?: ShippingOption;
}

export interface DomainEvent extends DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  _id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at `16:00` and then again at `16:01`, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
}

/** @oneof */
export interface DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
}

export interface EntityCreatedEvent {
  entity?: string;
}

export interface RestoreInfo {
  deletedDate?: Date | null;
}

export interface EntityUpdatedEvent {
  /**
   * Since platformized APIs only expose PATCH and not PUT we can't assume that the fields sent from the client are the actual diff.
   * This means that to generate a list of changed fields (as opposed to sent fields) one needs to traverse both objects.
   * We don't want to impose this on all developers and so we leave this traversal to the notification recipients which need it.
   */
  currentEntity?: string;
}

export interface EntityDeletedEvent {
  /** Entity that was deleted. */
  deletedEntity?: string | null;
}

export interface ActionEvent {
  body?: string;
}

export interface Empty {}

export interface AddDeliveryRegionRequest {
  /**
   * ID of the delivery region that should use this ShippingOption.
   * @format GUID
   */
  deliveryRegionId: string;
  /**
   * ID of the ShippingOption to be updated.
   * @format GUID
   */
  shippingOptionId: string;
  /** Revision of the ShippingOption. */
  revision: string | null;
}

export interface AddDeliveryRegionResponse {
  /** Updated ShippingOption. */
  shippingOption?: ShippingOption;
}

export interface RemoveDeliveryRegionRequest {
  /**
   * ID of the delivery region that should not use this ShippingOption anymore.
   * @format GUID
   */
  deliveryRegionId: string;
  /**
   * ID of the ShippingOption to be updated.
   * @format GUID
   */
  shippingOptionId: string;
  /** Revision of the ShippingOption. */
  revision: string | null;
}

export interface RemoveDeliveryRegionResponse {
  /** Updated ShippingOption. */
  shippingOption?: ShippingOption;
}

export interface MessageEnvelope {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Stringify payload. */
  data?: string;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface IdentificationData extends IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
  /** @readonly */
  identityType?: WebhookIdentityTypeWithLiterals;
}

/** @oneof */
export interface IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
}

export enum WebhookIdentityType {
  UNKNOWN = 'UNKNOWN',
  ANONYMOUS_VISITOR = 'ANONYMOUS_VISITOR',
  MEMBER = 'MEMBER',
  WIX_USER = 'WIX_USER',
  APP = 'APP',
}

/** @enumType */
export type WebhookIdentityTypeWithLiterals =
  | WebhookIdentityType
  | 'UNKNOWN'
  | 'ANONYMOUS_VISITOR'
  | 'MEMBER'
  | 'WIX_USER'
  | 'APP';

export interface AccountInfo {
  /**
   * ID of the Wix account associated with the event.
   * @format GUID
   */
  accountId?: string | null;
  /**
   * ID of the parent Wix account. Only included when accountId belongs to a child account.
   * @format GUID
   */
  parentAccountId?: string | null;
  /**
   * ID of the Wix site associated with the event. Only included when the event is tied to a specific site.
   * @format GUID
   */
  siteId?: string | null;
}

/** @docsIgnore */
export type CreateShippingOptionApplicationErrors = {
  code?: 'INVALID_QUANTITY_VALUE';
  description?: string;
  data?: Record<string, any>;
};
/** @docsIgnore */
export type UpdateShippingOptionApplicationErrors = {
  code?: 'INVALID_QUANTITY_VALUE';
  description?: string;
  data?: Record<string, any>;
};

export interface BaseEventMetadata {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface EventMetadata extends BaseEventMetadata {
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  _id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at `16:00` and then again at `16:01`, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
  accountInfo?: AccountInfoMetadata;
}

export interface AccountInfoMetadata {
  /** ID of the Wix account associated with the event */
  accountId: string;
  /** ID of the Wix site associated with the event. Only included when the event is tied to a specific site. */
  siteId?: string;
  /** ID of the parent Wix account. Only included when 'accountId' belongs to a child account. */
  parentAccountId?: string;
}

export interface ShippingOptionCreatedEnvelope {
  entity: ShippingOption;
  metadata: EventMetadata;
}

/**
 * Triggered when a shipping option is created.
 * @permissionScope Manage Stores - all permissions
 * @permissionScopeId SCOPE.DC-STORES-MEGA.MANAGE-STORES
 * @permissionScope Manage Stores
 * @permissionScopeId SCOPE.STORES.MANAGE-STORES
 * @permissionScope Read eCommerce - all read permissions
 * @permissionScopeId SCOPE.DC-ECOM-MEGA.READ-ECOM
 * @permissionScope Manage Restaurants - all permissions
 * @permissionScopeId SCOPE.RESTAURANTS.MEGA-SCOPES
 * @permissionScope Manage eCommerce - all permissions
 * @permissionScopeId SCOPE.DC-ECOM-MEGA.MANAGE-ECOM
 * @permissionId ECOM.SHIPPING_OPTION_READ
 * @webhook
 * @eventType wix.ecom.v1.shipping_option_created
 * @slug created
 * @documentationMaturity preview
 */
export declare function onShippingOptionCreated(
  handler: (event: ShippingOptionCreatedEnvelope) => void | Promise<void>
): void;

export interface ShippingOptionDeletedEnvelope {
  entity: ShippingOption;
  metadata: EventMetadata;
}

/**
 * Triggered when a shipping option is deleted.
 * @permissionScope Manage Stores - all permissions
 * @permissionScopeId SCOPE.DC-STORES-MEGA.MANAGE-STORES
 * @permissionScope Manage Stores
 * @permissionScopeId SCOPE.STORES.MANAGE-STORES
 * @permissionScope Read eCommerce - all read permissions
 * @permissionScopeId SCOPE.DC-ECOM-MEGA.READ-ECOM
 * @permissionScope Manage Restaurants - all permissions
 * @permissionScopeId SCOPE.RESTAURANTS.MEGA-SCOPES
 * @permissionScope Manage eCommerce - all permissions
 * @permissionScopeId SCOPE.DC-ECOM-MEGA.MANAGE-ECOM
 * @permissionId ECOM.SHIPPING_OPTION_READ
 * @webhook
 * @eventType wix.ecom.v1.shipping_option_deleted
 * @slug deleted
 * @documentationMaturity preview
 */
export declare function onShippingOptionDeleted(
  handler: (event: ShippingOptionDeletedEnvelope) => void | Promise<void>
): void;

export interface ShippingOptionUpdatedEnvelope {
  entity: ShippingOption;
  metadata: EventMetadata;
  /** @hidden */
  modifiedFields: Record<string, any>;
}

/**
 * Triggered when a shipping option is updated.
 * @permissionScope Manage Stores - all permissions
 * @permissionScopeId SCOPE.DC-STORES-MEGA.MANAGE-STORES
 * @permissionScope Manage Stores
 * @permissionScopeId SCOPE.STORES.MANAGE-STORES
 * @permissionScope Read eCommerce - all read permissions
 * @permissionScopeId SCOPE.DC-ECOM-MEGA.READ-ECOM
 * @permissionScope Manage Restaurants - all permissions
 * @permissionScopeId SCOPE.RESTAURANTS.MEGA-SCOPES
 * @permissionScope Manage eCommerce - all permissions
 * @permissionScopeId SCOPE.DC-ECOM-MEGA.MANAGE-ECOM
 * @permissionId ECOM.SHIPPING_OPTION_READ
 * @webhook
 * @eventType wix.ecom.v1.shipping_option_updated
 * @slug updated
 * @documentationMaturity preview
 */
export declare function onShippingOptionUpdated(
  handler: (event: ShippingOptionUpdatedEnvelope) => void | Promise<void>
): void;

/**
 * Creates a ShippingOption.
 *
 * The request body must include shipping option and delivery region associated to.
 * @param shippingOption - ShippingOption to be created.
 * @public
 * @documentationMaturity preview
 * @requiredField shippingOption
 * @permissionId ECOM.SHIPPING_OPTION_CREATE
 * @applicableIdentity APP
 * @returns The created ShippingOption.
 * @fqn wix.ecom.delivery.v1.ShippingOptionService.CreateShippingOption
 */
export async function createShippingOption(
  shippingOption: ShippingOption
): Promise<
  NonNullablePaths<
    ShippingOption,
    | `deliveryRegionId`
    | `deliveryRegionIds`
    | `rates`
    | `rates.${number}.amount`
    | `rates.${number}.multiplyByQuantity`,
    4
  > & {
    __applicationErrorsType?: CreateShippingOptionApplicationErrors;
  }
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    shippingOption: shippingOption,
  });

  const reqOpts =
    ambassadorWixEcomV1ShippingOption.createShippingOption(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)
      ?.shippingOption!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { shippingOption: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['shippingOption']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Retrieves a ShippingOption.
 * @param shippingOptionId - ID of the ShippingOption to retrieve.
 * @public
 * @documentationMaturity preview
 * @requiredField shippingOptionId
 * @permissionId ECOM.SHIPPING_OPTION_READ
 * @applicableIdentity APP
 * @returns The requested ShippingOption.
 * @fqn wix.ecom.delivery.v1.ShippingOptionService.GetShippingOption
 */
export async function getShippingOption(
  shippingOptionId: string
): Promise<
  NonNullablePaths<
    ShippingOption,
    | `deliveryRegionId`
    | `deliveryRegionIds`
    | `rates`
    | `rates.${number}.amount`
    | `rates.${number}.multiplyByQuantity`,
    4
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    shippingOptionId: shippingOptionId,
  });

  const reqOpts = ambassadorWixEcomV1ShippingOption.getShippingOption(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)
      ?.shippingOption!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { shippingOptionId: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['shippingOptionId']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Updates a ShippingOption.
 *
 *
 * Each time the ShippingOption is updated,
 * `revision` increments by 1.
 * The current `revision` must be passed when updating the ShippingOption.
 * This ensures you're working with the latest ShippingOption
 * and prevents unintended overwrites.
 * @param _id - ShippingOption ID.
 * @public
 * @documentationMaturity preview
 * @requiredField _id
 * @requiredField shippingOption
 * @requiredField shippingOption.revision
 * @permissionId ECOM.SHIPPING_OPTION_UPDATE
 * @applicableIdentity APP
 * @returns Updated ShippingOption.
 * @fqn wix.ecom.delivery.v1.ShippingOptionService.UpdateShippingOption
 */
export async function updateShippingOption(
  _id: string,
  shippingOption: NonNullablePaths<UpdateShippingOption, `revision`, 2>
): Promise<
  NonNullablePaths<
    ShippingOption,
    | `deliveryRegionId`
    | `deliveryRegionIds`
    | `rates`
    | `rates.${number}.amount`
    | `rates.${number}.multiplyByQuantity`,
    4
  > & {
    __applicationErrorsType?: UpdateShippingOptionApplicationErrors;
  }
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    shippingOption: { ...shippingOption, id: _id },
  });

  const reqOpts =
    ambassadorWixEcomV1ShippingOption.updateShippingOption(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)
      ?.shippingOption!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: { shippingOption: '$[1]' },
        explicitPathsToArguments: { 'shippingOption.id': '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['_id', 'shippingOption']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface UpdateShippingOption {
  /**
   * ShippingOption ID.
   * @format GUID
   * @readonly
   */
  _id?: string | null;
  /**
   * Revision number, which increments by 1 each time the ShippingOption is updated.
   * To prevent conflicting changes,
   * the current revision must be passed when updating the ShippingOption.
   *
   * Ignored when creating a ShippingOption.
   * @readonly
   */
  revision?: string | null;
  /**
   * Date and time the ShippingOption was created.
   * @readonly
   */
  _createdDate?: Date | null;
  /**
   * Date and time the ShippingOption was last updated.
   * @readonly
   */
  _updatedDate?: Date | null;
  /**
   * Associated delivery region id.
   * @format GUID
   * @immutable
   * @deprecated Associated delivery region id.
   * @replacedBy delivery_region_ids
   * @targetRemovalDate 2025-06-01
   */
  deliveryRegionId?: string;
  /**
   * This shipping option is active for the following delivery regions.
   * @maxSize 50
   * @format GUID
   * @immutable
   */
  deliveryRegionIds?: string[];
  /**
   * Delivery option configured for the ShippingOption
   * @maxLength 1000
   */
  title?: string | null;
  /**
   * Estimated delivery time. For example, "2-3 business days".
   * @maxLength 500
   */
  estimatedDeliveryTime?: string | null;
  /**
   * at runtime for a given shipment input, up to one rate (price) should be returned in an option. If more than one rate is valid then we return the lowest one.
   * @minSize 1
   * @maxSize 50
   */
  rates?: ConditionalRates[];
  /** Data Extensions */
  extendedFields?: ExtendedFields;
}

/**
 * Deletes a ShippingOption.
 * Deleting a ShippingOption permanently removes them from the ShippingOption List.
 * @param shippingOptionId - Id of the ShippingOption to delete.
 * @public
 * @documentationMaturity preview
 * @requiredField shippingOptionId
 * @permissionId ECOM.SHIPPING_OPTION_DELETE
 * @applicableIdentity APP
 * @fqn wix.ecom.delivery.v1.ShippingOptionService.DeleteShippingOption
 */
export async function deleteShippingOption(
  shippingOptionId: string
): Promise<void> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    shippingOptionId: shippingOptionId,
  });

  const reqOpts =
    ambassadorWixEcomV1ShippingOption.deleteShippingOption(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { shippingOptionId: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['shippingOptionId']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Retrieves a list of ShippingOptions, given the provided [paging, filtering, and sorting][1].
 *
 * Up to 1,000 ShippingOptions can be returned per request.
 *
 * To learn how to query ShippingOptions, see [API Query Language][2].
 *
 * [1]: https://dev.wix.com/api/rest/getting-started/sorting-and-paging
 * [2]: https://dev.wix.com/api/rest/getting-started/api-query-language
 * @public
 * @documentationMaturity preview
 * @permissionId ECOM.SHIPPING_OPTION_READ
 * @applicableIdentity APP
 * @fqn wix.ecom.delivery.v1.ShippingOptionService.QueryShippingOptions
 */
export function queryShippingOptions(): ShippingOptionsQueryBuilder {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[0] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  return queryBuilder<
    ShippingOption,
    'CURSOR',
    QueryShippingOptionsRequest,
    QueryShippingOptionsResponse
  >({
    func: async (payload: QueryShippingOptionsRequest) => {
      const reqOpts =
        ambassadorWixEcomV1ShippingOption.queryShippingOptions(payload);

      sideEffects?.onSiteCall?.();
      try {
        const result = await httpClient.request(reqOpts);
        sideEffects?.onSuccess?.(result);
        return result;
      } catch (err) {
        sideEffects?.onError?.(err);
        throw err;
      }
    },
    requestTransformer: (query: QueryShippingOptionsRequest['query']) => {
      const args = [query, {}] as [QueryShippingOptionsRequest['query'], {}];
      return renameKeysFromSDKRequestToRESTRequest({
        ...args?.[1],
        query: args?.[0],
      });
    },
    responseTransformer: ({
      data,
    }: HttpResponse<QueryShippingOptionsResponse>) => {
      const transformedData = renameKeysFromRESTResponseToSDKResponse(
        transformPaths(data, [])
      );

      return {
        items: transformedData?.shippingOptions,
        pagingMetadata: transformedData?.pagingMetadata,
      };
    },
    errorTransformer: (err: unknown) => {
      const transformedError = sdkTransformError(err, {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { query: '$[0]' },
        singleArgumentUnchanged: false,
      });

      throw transformedError;
    },
    pagingMethod: 'CURSOR',
    transformationPaths: {},
  });
}

interface QueryCursorResult {
  cursors: Cursors;
  hasNext: () => boolean;
  hasPrev: () => boolean;
  length: number;
  pageSize: number;
}

export interface ShippingOptionsQueryResult extends QueryCursorResult {
  items: ShippingOption[];
  query: ShippingOptionsQueryBuilder;
  next: () => Promise<ShippingOptionsQueryResult>;
  prev: () => Promise<ShippingOptionsQueryResult>;
}

export interface ShippingOptionsQueryBuilder {
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   * @documentationMaturity preview
   */
  eq: (
    propertyName:
      | '_id'
      | '_createdDate'
      | '_updatedDate'
      | 'deliveryRegionId'
      | 'estimatedDeliveryTime',
    value: any
  ) => ShippingOptionsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   * @documentationMaturity preview
   */
  ne: (
    propertyName:
      | '_id'
      | '_createdDate'
      | '_updatedDate'
      | 'deliveryRegionId'
      | 'estimatedDeliveryTime',
    value: any
  ) => ShippingOptionsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   * @documentationMaturity preview
   */
  ge: (
    propertyName:
      | '_id'
      | '_createdDate'
      | '_updatedDate'
      | 'deliveryRegionId'
      | 'estimatedDeliveryTime',
    value: any
  ) => ShippingOptionsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   * @documentationMaturity preview
   */
  gt: (
    propertyName:
      | '_id'
      | '_createdDate'
      | '_updatedDate'
      | 'deliveryRegionId'
      | 'estimatedDeliveryTime',
    value: any
  ) => ShippingOptionsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   * @documentationMaturity preview
   */
  le: (
    propertyName:
      | '_id'
      | '_createdDate'
      | '_updatedDate'
      | 'deliveryRegionId'
      | 'estimatedDeliveryTime',
    value: any
  ) => ShippingOptionsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   * @documentationMaturity preview
   */
  lt: (
    propertyName:
      | '_id'
      | '_createdDate'
      | '_updatedDate'
      | 'deliveryRegionId'
      | 'estimatedDeliveryTime',
    value: any
  ) => ShippingOptionsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `string`.
   * @param string - String to compare against. Case-insensitive.
   * @documentationMaturity preview
   */
  startsWith: (
    propertyName: '_id' | 'deliveryRegionId' | 'estimatedDeliveryTime',
    value: string
  ) => ShippingOptionsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `values`.
   * @param values - List of values to compare against.
   * @documentationMaturity preview
   */
  hasSome: (
    propertyName: 'deliveryRegionIds',
    value: any[]
  ) => ShippingOptionsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `values`.
   * @param values - List of values to compare against.
   * @documentationMaturity preview
   */
  hasAll: (
    propertyName: 'deliveryRegionIds',
    value: any[]
  ) => ShippingOptionsQueryBuilder;
  /** @documentationMaturity preview */
  in: (
    propertyName:
      | '_id'
      | '_createdDate'
      | '_updatedDate'
      | 'deliveryRegionId'
      | 'estimatedDeliveryTime',
    value: any
  ) => ShippingOptionsQueryBuilder;
  /** @documentationMaturity preview */
  exists: (
    propertyName:
      | '_id'
      | '_createdDate'
      | '_updatedDate'
      | 'deliveryRegionId'
      | 'deliveryRegionIds'
      | 'estimatedDeliveryTime',
    value: boolean
  ) => ShippingOptionsQueryBuilder;
  /** @param propertyNames - Properties used in the sort. To sort by multiple properties, pass properties as additional arguments.
   * @documentationMaturity preview
   */
  ascending: (
    ...propertyNames: Array<
      | '_id'
      | '_createdDate'
      | '_updatedDate'
      | 'deliveryRegionId'
      | 'deliveryRegionIds'
      | 'estimatedDeliveryTime'
    >
  ) => ShippingOptionsQueryBuilder;
  /** @param propertyNames - Properties used in the sort. To sort by multiple properties, pass properties as additional arguments.
   * @documentationMaturity preview
   */
  descending: (
    ...propertyNames: Array<
      | '_id'
      | '_createdDate'
      | '_updatedDate'
      | 'deliveryRegionId'
      | 'deliveryRegionIds'
      | 'estimatedDeliveryTime'
    >
  ) => ShippingOptionsQueryBuilder;
  /** @param limit - Number of items to return, which is also the `pageSize` of the results object.
   * @documentationMaturity preview
   */
  limit: (limit: number) => ShippingOptionsQueryBuilder;
  /** @param cursor - A pointer to specific record
   * @documentationMaturity preview
   */
  skipTo: (cursor: string) => ShippingOptionsQueryBuilder;
  /** @documentationMaturity preview */
  find: () => Promise<ShippingOptionsQueryResult>;
}

/**
 * @hidden
 * @fqn wix.ecom.delivery.v1.ShippingOptionService.QueryShippingOptions
 * @requiredField query
 */
export async function typedQueryShippingOptions(
  query: ShippingOptionQuery
): Promise<
  NonNullablePaths<
    QueryShippingOptionsResponse,
    `shippingOptions` | `shippingOptions.${number}.deliveryRegionId`,
    4
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({ query: query });

  const reqOpts =
    ambassadorWixEcomV1ShippingOption.queryShippingOptions(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { query: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['query']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface ShippingOptionQuerySpec extends QuerySpec {
  paging: 'cursor';
  wql: [
    {
      fields: ['deliveryRegionIds'];
      operators: ['$hasAll', '$hasSome'];
      sort: 'BOTH';
    },
    {
      fields: [
        '_createdDate',
        '_id',
        '_updatedDate',
        'deliveryRegionId',
        'estimatedDeliveryTime'
      ];
      operators: '*';
      sort: 'BOTH';
    }
  ];
}

export type CommonQueryWithEntityContext = QuerySdkType<
  ShippingOption,
  ShippingOptionQuerySpec
>;
export type ShippingOptionQuery = {
  /** 
  Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`.  
  */
  cursorPaging?: {
    /** 
  Maximum number of items to return in the results. 
  @max: 100 
  */
    limit?:
      | NonNullable<CommonQueryWithEntityContext['cursorPaging']>['limit']
      | null;
    /** 
  Pointer to the next or previous page in the list of results.

  Pass the relevant cursor token from the `pagingMetadata` object in the previous call's response.
  Not relevant for the first request. 
  @maxLength: 16000 
  */
    cursor?:
      | NonNullable<CommonQueryWithEntityContext['cursorPaging']>['cursor']
      | null;
  };
  /** 
  Filter object in the following format:
  `"filter" : {
  "fieldName1": "value1",
  "fieldName2":{"$operator":"value2"}
  }`
  Example of operators: `$eq`, `$ne`, `$lt`, `$lte`, `$gt`, `$gte`, `$in`, `$hasSome`, `$hasAll`, `$startsWith`, `$contains`  
  */
  filter?: CommonQueryWithEntityContext['filter'] | null;
  /** 
  Sort object in the following format:
  `[{"fieldName":"sortField1","order":"ASC"},{"fieldName":"sortField2","order":"DESC"}]` 
  @maxSize: 5 
  */
  sort?: {
    /** 
  Name of the field to sort by. 
  @maxLength: 512 
  */
    fieldName?: NonNullable<
      CommonQueryWithEntityContext['sort']
    >[number]['fieldName'];
    /** 
  Sort order.  
  */
    order?: NonNullable<CommonQueryWithEntityContext['sort']>[number]['order'];
  }[];
};

export const utils = {
  query: /*#__PURE__*/ createQueryUtils<
    ShippingOption,
    ShippingOptionQuerySpec,
    ShippingOptionQuery
  >(),
};

/**
 * Updates extended fields of a ShippingOption without incrementing revision
 * @param _id - ID of the entity to update.
 * @param namespace - Identifier for the app whose extended fields are being updated.
 * @public
 * @documentationMaturity preview
 * @requiredField _id
 * @requiredField namespace
 * @requiredField options
 * @requiredField options.namespaceData
 * @permissionId ECOM.SHIPPING_OPTION_UPDATE
 * @applicableIdentity APP
 * @fqn wix.ecom.delivery.v1.ShippingOptionService.UpdateExtendedFields
 */
export async function updateExtendedFields(
  _id: string,
  namespace: string,
  options: NonNullablePaths<UpdateExtendedFieldsOptions, `namespaceData`, 2>
): Promise<
  NonNullablePaths<
    UpdateExtendedFieldsResponse,
    | `shippingOption.deliveryRegionId`
    | `shippingOption.deliveryRegionIds`
    | `shippingOption.rates`
    | `shippingOption.rates.${number}.amount`
    | `shippingOption.rates.${number}.multiplyByQuantity`,
    5
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[3] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    id: _id,
    namespace: namespace,
    namespaceData: options?.namespaceData,
  });

  const reqOpts =
    ambassadorWixEcomV1ShippingOption.updateExtendedFields(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          id: '$[0]',
          namespace: '$[1]',
          namespaceData: '$[2].namespaceData',
        },
        singleArgumentUnchanged: false,
      },
      ['_id', 'namespace', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface UpdateExtendedFieldsOptions {
  /** Data to update. Structured according to the [schema](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields#json-schema-for-extended-fields) defined when the extended fields were configured. */
  namespaceData: Record<string, any> | null;
}

/**
 * Adds a delivery region to a shipping option.
 * @param shippingOptionId - ID of the ShippingOption to be updated.
 * @param deliveryRegionId - ID of the delivery region that should use this ShippingOption.
 * @public
 * @documentationMaturity preview
 * @requiredField deliveryRegionId
 * @requiredField options
 * @requiredField options.revision
 * @requiredField shippingOptionId
 * @permissionId ECOM.SHIPPING_OPTION_UPDATE
 * @applicableIdentity APP
 * @fqn wix.ecom.delivery.v1.ShippingOptionService.AddDeliveryRegion
 */
export async function addDeliveryRegion(
  shippingOptionId: string,
  deliveryRegionId: string,
  options: NonNullablePaths<AddDeliveryRegionOptions, `revision`, 2>
): Promise<
  NonNullablePaths<
    AddDeliveryRegionResponse,
    | `shippingOption.deliveryRegionId`
    | `shippingOption.deliveryRegionIds`
    | `shippingOption.rates`
    | `shippingOption.rates.${number}.amount`
    | `shippingOption.rates.${number}.multiplyByQuantity`,
    5
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[3] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    shippingOptionId: shippingOptionId,
    deliveryRegionId: deliveryRegionId,
    revision: options?.revision,
  });

  const reqOpts = ambassadorWixEcomV1ShippingOption.addDeliveryRegion(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          shippingOptionId: '$[0]',
          deliveryRegionId: '$[1]',
          revision: '$[2].revision',
        },
        singleArgumentUnchanged: false,
      },
      ['shippingOptionId', 'deliveryRegionId', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface AddDeliveryRegionOptions {
  /** Revision of the ShippingOption. */
  revision: string | null;
}

/**
 * Removes a delivery region from a shipping option.
 * @param shippingOptionId - ID of the ShippingOption to be updated.
 * @param deliveryRegionId - ID of the delivery region that should not use this ShippingOption anymore.
 * @public
 * @documentationMaturity preview
 * @requiredField deliveryRegionId
 * @requiredField options
 * @requiredField options.revision
 * @requiredField shippingOptionId
 * @permissionId ECOM.SHIPPING_OPTION_UPDATE
 * @applicableIdentity APP
 * @fqn wix.ecom.delivery.v1.ShippingOptionService.RemoveDeliveryRegion
 */
export async function removeDeliveryRegion(
  shippingOptionId: string,
  deliveryRegionId: string,
  options: NonNullablePaths<RemoveDeliveryRegionOptions, `revision`, 2>
): Promise<
  NonNullablePaths<
    RemoveDeliveryRegionResponse,
    | `shippingOption.deliveryRegionId`
    | `shippingOption.deliveryRegionIds`
    | `shippingOption.rates`
    | `shippingOption.rates.${number}.amount`
    | `shippingOption.rates.${number}.multiplyByQuantity`,
    5
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[3] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    shippingOptionId: shippingOptionId,
    deliveryRegionId: deliveryRegionId,
    revision: options?.revision,
  });

  const reqOpts =
    ambassadorWixEcomV1ShippingOption.removeDeliveryRegion(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          shippingOptionId: '$[0]',
          deliveryRegionId: '$[1]',
          revision: '$[2].revision',
        },
        singleArgumentUnchanged: false,
      },
      ['shippingOptionId', 'deliveryRegionId', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface RemoveDeliveryRegionOptions {
  /** Revision of the ShippingOption. */
  revision: string | null;
}
